/**
 * SourceDetailPage — one intel source: overview, collected content (reader), signals, monitor, ask, settings.
 * Demo pages: `/connected-apps` (an app's header with status and actions), `/email` (the content reader tab).
 * Template: DetailPage. The tab is kept in the URL (`?tab=`), the source in `?source=` (see IntelSources.tsx).
 */
import React from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, Users, Zap } from 'lucide-react';
import {
  fetchIntelSourceMutation,
  getIntelSourceHarvestedOptions,
  getIntelSourceOptions,
  updateIntelSourceMutation,
} from '../../../generated/api/@tanstack/react-query.gen.js';
import type { IntelSource } from '../../../types/intel-source.js';
import { Can } from '../../../components/auth/Can.js';
import { DetailPage } from '../../../components/page/DetailPage.js';
import { Badge, Button, Callout, Chip, EmptyState, Skeleton, Toggle, useToast } from '../../../components/ui';
import { IntelSourceAgent } from '../../../components/intel/IntelSourceAgent.js';
import { usePolicy } from '../../../hooks/usePolicy.js';
import { useTabParam } from '../../../hooks/useTabParam.js';
import { safeHref } from '../../../lib/safe-url.js';
import { PAGE_HELP } from '../../../help';
import { Radar } from 'lucide-react';
import { PlatGlyph } from './PlatGlyph.js';
import { OverviewTab } from './OverviewTab.js';
import { DataTab } from './DataTab.js';
import { WorkflowsTab } from './WorkflowsTab.js';
import { SettingsTab } from './SettingsTab.js';
import { MonitorPanel } from './MonitorPanel.js';
import { useHarvestedInvalidation, useIntelSourceInvalidation } from './intel-source-queries.js';

interface SourceDetailPageProps {
  sourceId: number;
  /** Leaves the detail view (back to the registry). */
  onClose: () => void;
}

const TAB_IDS = ['overview', 'content', 'signals', 'monitor', 'ask', 'settings'] as const;

function originLabel(source: IntelSource): string {
  if (source.seed) return 'Seed';
  if (source.discoveredAt) return 'Discovered';
  return 'Added by hand';
}

/** 412000 becomes "412k", 1200000 becomes "1.2M". */
function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}k`;
  return n.toString();
}

function sourceBadges(source: IntelSource) {
  const sq = source.signalQuality == null ? Number.NaN : parseFloat(source.signalQuality);
  const sqDisplay = Number.isFinite(sq) && sq > 0 ? Math.round(sq * 100) : null;
  return (
    <>
      <Badge variant={source.active ? 'success' : 'neutral'} dot>
        {source.active ? 'Active' : 'Inactive'}
      </Badge>
      <Chip tone={source.seed ? 'blue' : source.discoveredAt ? 'purple' : 'neutral'}>{originLabel(source)}</Chip>
      {sqDisplay !== null && <Badge variant="accent">Signal quality {sqDisplay}</Badge>}
      {source.followerCount != null && (
        <Badge variant="neutral">
          <Users size={12} className="mr-1 inline-block" aria-hidden="true" />
          {formatCompact(source.followerCount)} followers
        </Badge>
      )}
    </>
  );
}

function SourceMeta({ source }: { source: IntelSource }) {
  const profile = safeHref(source.profileUrl);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted">
      <PlatGlyph platform={source.platform} size={24} radius={6} />
      <span className="font-mono text-xs">@{source.handle}</span>
      <span className="capitalize">{source.platform}</span>
      <span>{source.category.replace(/_/g, ' ')}</span>
      {profile && (
        <a
          href={safeHref(source.profileUrl) ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-accent-text hover:underline"
        >
          Profile <ExternalLink size={12} aria-hidden="true" />
        </a>
      )}
    </div>
  );
}

export const SourceDetailPage: React.FC<SourceDetailPageProps> = ({ sourceId, onClose }) => {
  const canManage = usePolicy('CanManageAgents');
  const [tab, setTab] = useTabParam(TAB_IDS, 'overview');
  const { notify } = useToast();
  const invalidate = useIntelSourceInvalidation();
  const invalidateHarvested = useHarvestedInvalidation();

  const sourceQuery = useQuery({ ...getIntelSourceOptions({ path: { id: sourceId } }), staleTime: 30_000 });
  const { data: harvestMeta } = useQuery({
    ...getIntelSourceHarvestedOptions({ path: { id: sourceId }, query: { limit: 1 } }),
    staleTime: 60_000,
  });
  const harvestCount = harvestMeta?.total ?? 0;

  const toggleMutation = useMutation({
    ...updateIntelSourceMutation(),
    onSuccess: () => invalidate(sourceId),
    onError: () => notify('Could not change the source. Try again.', 'danger'),
  });
  const fetchNowMutation = useMutation({
    ...fetchIntelSourceMutation(),
    onSuccess: () => {
      invalidateHarvested(sourceId);
      notify('Fetch queued.');
    },
    onError: () => notify('Could not queue a fetch.', 'danger'),
  });

  const breadcrumbs = [
    { label: 'Knowledge' },
    { label: 'Intel sources', to: '/agents/intel-sources' },
    { label: sourceQuery.data ? (sourceQuery.data.name ?? sourceQuery.data.handle) : 'Source' },
  ];

  if (sourceQuery.isError) {
    return (
      <DetailPage title="Source" breadcrumbs={breadcrumbs}>
        <Callout
          tone="danger"
          title="Could not load this source"
          actions={
            <>
              <Button size="sm" variant="secondary" onClick={() => sourceQuery.refetch()}>
                Retry
              </Button>
              <Button size="sm" variant="ghost" onClick={onClose}>
                Back to sources
              </Button>
            </>
          }
        >
          It may have been removed, or the request failed.
        </Callout>
      </DetailPage>
    );
  }

  const source = sourceQuery.data;
  if (!source) {
    return (
      <DetailPage title="Source" breadcrumbs={breadcrumbs}>
        <div className="space-y-3">
          <Skeleton height={24} width="30%" />
          <Skeleton height={120} />
        </div>
      </DetailPage>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'content', label: 'Content', count: harvestCount > 0 ? harvestCount : undefined },
    {
      id: 'signals',
      label: 'Signals',
      count: (source.totalSignals ?? 0) > 0 ? (source.totalSignals ?? undefined) : undefined,
    },
    { id: 'monitor', label: 'Monitor' },
    { id: 'ask', label: 'Ask' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <DetailPage
      title={source.name ?? source.handle}
      breadcrumbs={breadcrumbs}
      helpContent={PAGE_HELP['intel-sources']}
      badges={sourceBadges(source)}
      meta={<SourceMeta source={source} />}
      actions={
        <Can policy="CanManageAgents">
          <div className="flex items-center gap-3">
            <Toggle
              checked={source.active}
              disabled={toggleMutation.isPending}
              onChange={(active) => toggleMutation.mutate({ path: { id: sourceId }, body: { active } })}
              label={source.active ? 'Active' : 'Inactive'}
            />
            <Button
              variant="secondary"
              size="sm"
              loading={fetchNowMutation.isPending}
              onClick={() => fetchNowMutation.mutate({ path: { id: sourceId }, body: {} })}
            >
              <Zap size={14} aria-hidden="true" /> Fetch now
            </Button>
          </div>
        </Can>
      }
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
    >
      {tab === 'overview' && <OverviewTab source={source} />}
      {tab === 'content' && <DataTab sourceId={source.id} />}
      {tab === 'signals' && <WorkflowsTab totalSignals={source.totalSignals ?? 0} />}
      {tab === 'monitor' && <MonitorPanel intelSourceId={source.id} />}
      {tab === 'ask' && (
        // The stream route requires both policies.
        <Can
          policies={['CanExecuteWorkflows', 'CanViewAgents']}
          fallback={
            <EmptyState
              icon={Radar}
              title="Asking is not available"
              description="Asking about a source needs permission to view agents and run workflows."
            />
          }
        >
          <IntelSourceAgent sourceId={source.id} sourceName={source.name ?? source.handle} platform={source.platform} />
        </Can>
      )}
      {tab === 'settings' && (
        <SettingsTab source={source} canManage={canManage} onUpdated={() => invalidate(sourceId)} onClose={onClose} />
      )}
    </DetailPage>
  );
};
